import { useCallback, useRef } from "react";

const CAPTURE_THRESHOLD = 10;
const FIRE_THRESHOLD = 80;

export interface UseSwipeToReplyResult {
  ref: React.RefObject<HTMLDivElement | null>;
  iconRef: React.RefObject<HTMLDivElement | null>;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

export function useSwipeToReply(onReply: () => void): UseSwipeToReplyResult {
  const ref = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const captured = useRef(false);
  const fired = useRef(false);
  const pointerId = useRef<number | null>(null);

  const reset = useCallback((el: HTMLDivElement | null, icon: HTMLDivElement | null) => {
    if (el) {
      el.style.transition = "transform 0.2s ease-out";
      el.style.transform = "";
    }
    if (icon) {
      icon.style.transition = "opacity 0.2s ease-out";
      icon.style.opacity = "0";
    }
    captured.current = false;
    fired.current = false;
    pointerId.current = null;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== "touch") return;
    startX.current = e.clientX;
    captured.current = false;
    fired.current = false;
    pointerId.current = e.pointerId;
    const el = ref.current;
    if (el) {
      el.style.transition = "none";
    }
    const icon = iconRef.current;
    if (icon) {
      icon.style.transition = "none";
    }
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (pointerId.current !== e.pointerId) return;
    const dx = startX.current - e.clientX;
    if (dx < 0) return;

    if (!captured.current) {
      if (dx >= CAPTURE_THRESHOLD) {
        captured.current = true;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }
      return;
    }

    const clamped = Math.min(dx, FIRE_THRESHOLD + 20);
    const el = ref.current;
    if (el) {
      el.style.transform = `translateX(${-clamped}px)`;
    }
    const icon = iconRef.current;
    if (icon) {
      icon.style.opacity = String(Math.min(clamped / FIRE_THRESHOLD, 1));
    }

    // Past the threshold the gesture has committed: buzz now so the hand gets
    // its confirmation mid-drag, but hold the reply itself until release.
    if (!fired.current && dx >= FIRE_THRESHOLD) {
      fired.current = true;
      navigator.vibrate?.(10);
    }
  }, []);

  // The reply fires here, not at the threshold, so it runs inside a pointerup
  // — one of the few events a mobile browser counts as a user gesture. iOS
  // only raises the on-screen keyboard for a focus() made from inside such a
  // gesture, and a pointermove is not one, so a reply committed mid-drag left
  // the composer focused with no keyboard behind it.
  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (pointerId.current !== e.pointerId) return;
    const commit = fired.current;
    reset(ref.current, iconRef.current);
    if (commit) onReply();
  }, [reset, onReply]);

  // A cancel is the browser taking the gesture away mid-swipe. The buzz has
  // already promised a reply, so honour it — the keyboard can't follow here,
  // since no user gesture is in flight, but the reply bar still opens.
  const onPointerCancel = useCallback((e: React.PointerEvent) => {
    if (pointerId.current !== e.pointerId) return;
    const commit = fired.current;
    reset(ref.current, iconRef.current);
    if (commit) onReply();
  }, [reset, onReply]);

  return { ref, iconRef, onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
